/**
 * blocky-avatar.js
 * Lớp khởi tạo mô hình 3D hình hộp (Blocky / Voxel Humanoid)
 * Hỗ trợ Character Customization toàn diện: Dáng người (Body Morphs),
 * Kiểu tóc, Mũ nón Việt Nam, Kính mắt, Khẩu trang, Phụ kiện thân và Đồ vật cầm tay.
 */

import * as THREE from 'three';
import { generateProceduralTexture, AVATAR_PRESETS, getDefaultShirtColor } from './avatar-presets.js';
import { AvatarAnimator } from './avatar-animator.js';
import { AvatarItemsLibrary } from './avatar-items-library.js';
import { enforceShadowPolicy } from '../core/blob-shadow.js';
import { disposeObject3D } from '../core/dispose.js';
import { buildAvatarHierarchy } from './avatar-builder/avatar-builder.js';
import { createSpeechBubble, speak, hideSpeech } from './speech-bubble.js';
import { registerAvatarAccessoriesMethods } from './blocky-avatar-accessories.js';
import { registerAvatarOutfitMethods } from './blocky-avatar-outfit.js';
import { registerAvatarHeldItemMethods } from './blocky-avatar-held-items.js';

export class BlockyAvatar {
  /**
   * @param {string|object} preset - ID preset hoặc config object
   * @param {object} customOptions - tùy biến ghi đè
   */
  constructor(preset = 'TEACHER', customOptions = {}) {
    const basePreset = typeof preset === 'string' ? AVATAR_PRESETS[preset] || AVATAR_PRESETS.TEACHER : preset;

    this.config = {
      ...basePreset,
      ...customOptions,
      colors: { ...basePreset.colors, ...(customOptions.colors || {}) },
      features: { ...basePreset.features, ...(customOptions.features || {}) }
    };

    // Tự động đồng bộ màu áo nếu outfit được chỉ định mới mà không truyền kèm màu áo cụ thể
    if (customOptions.features && customOptions.features.outfit) {
      if (!customOptions.colors || !customOptions.colors.shirt) {
        if (customOptions.features.outfit !== basePreset.features?.outfit) {
          this.config.colors.shirt = getDefaultShirtColor(customOptions.features.outfit);
        }
      }
    }

    // Chế độ cấu trúc chi: Articulated (có khớp gối & khuỷu tay) hoặc Classic (khối hộp biped truyền thống)
    this.isArticulated =
      customOptions.isArticulated !== undefined
        ? !!customOptions.isArticulated
        : this.config.isArticulated !== undefined
          ? !!this.config.isArticulated
          : true;

    this.root = new THREE.Group();
    this.root.name = `Avatar_${this.config.id || 'Custom'}`;

    this.itemsLib = new AvatarItemsLibrary();
    this.parts = {};
    this.disposables = [];

    // Lưu các slot phụ kiện động
    this.attachmentSlots = {
      hair: null,
      hat: null,
      eyewear: null,
      mask: null,
      beard: null,
      bodyAcc: null,
      heldItem: null,
      horns: null,
      wings: null,
      tail: null
    };

    // Dựng hình học và cây phân cấp
    this.buildHierarchy();

    // Khởi tạo animator
    this.parts.elderPosture = this.config.bodyType === 'elder';
    this.animator = new AvatarAnimator(this.parts);

    // Hệ thống bóng thoại
    this.speechTimeout = null;
    this.createSpeechBubble();

    // Gắn các phụ kiện mặc định từ preset
    this.applyInitialAttachments();

    // Khởi tạo Fake Blob Shadow mềm mại dưới chân nhân vật
    this.createBlobShadow();

    // Thực thi chính sách bóng: castShadow = false (không tự đổ bóng lên cơ thể), receiveShadow = true (nhận bóng râm môi trường)
    this.enforceShadowPolicy();
  }

  buildHierarchy() {
    buildAvatarHierarchy(this);
  }

  /**
   * Áp dụng các phụ kiện khởi tạo từ config
   */
  applyInitialAttachments() {
    const f = this.config.features;
    // 1. Tóc
    if (f.hairStyle) {
      this.setHair(f.hairStyle, this.config.colors.hair);
    } else {
      this.setHair('neat_parted', this.config.colors.hair);
    }
    // 2. Mũ / Nón
    if (f.hat) {
      this.setHat(f.hat);
    }
    // 3. Kính mắt
    if (f.eyewear) {
      this.setEyewear(f.eyewear);
    } else if (f.hasGlasses) {
      this.setEyewear('kinh_can');
    }
    // 4. Khẩu trang
    if (f.mask) {
      this.setMask(f.mask);
    }
    // 4b. Râu (elder_beard hoặc râu tùy biến)
    if (f.beard) {
      this.setBeard(f.beard, this.config.colors.hair || '#d8d8d8');
    }
    // 5. Phụ kiện thân (Ba lô, túi xách, vali...)
    if (f.bodyAccessory) {
      this.setBodyAccessory(f.bodyAccessory);
    } else if (f.hasBackpack) {
      this.setBodyAccessory('ba_lo');
    }
    // 6. Đồ vật cầm tay
    if (f.heldItem) {
      this.setHeldItem(f.heldItem);
    } else if (f.hasClipboard) {
      this.setHeldItem('clipboard');
    }
    // 7. Sừng ngoài hành tinh (hỗ trợ scaleFactor hoặc tự động scale cho child)
    if (f.horns) {
      this.setHorns(f.horns, this.config.colors.horns || this.config.colors.hair, f.scaleFactor);
    }
    // 8. Cánh & Vây ngoài hành tinh (hỗ trợ scaleFactor hoặc tự động scale cho child)
    if (f.wings) {
      this.setWings(f.wings, this.config.colors.wings || this.config.colors.shirt, f.scaleFactor);
    }
    // 9. Đuôi ngoài hành tinh
    if (f.tail) {
      this.setTail(f.tail, this.config.colors.tail || this.config.colors.skin);
    }
  }

  updateHeadTextures() {
    const hairStyle = this.config.features.hairStyle;
    const hairColor = this.config.colors.hair;
    const skinColor = this.config.colors.skin;

    // 1. Cập nhật mặt trước (face)
    this.setExpression(this.currentExpression || 'happy');

    // 2. Cập nhật mặt bên phải (+X)
    if (this.headSideRightCanvas && this.headSideRightTex) {
      const rightCanvas = generateProceduralTexture('head_side', {
        skinColor,
        hairColor,
        hairStyle,
        side: 'right'
      });
      const ctx = this.headSideRightCanvas.getContext('2d');
      ctx.clearRect(0, 0, this.headSideRightCanvas.width, this.headSideRightCanvas.height);
      ctx.drawImage(rightCanvas, 0, 0);
      this.headSideRightTex.needsUpdate = true;
    }

    // 3. Cập nhật mặt bên trái (-X)
    if (this.headSideLeftCanvas && this.headSideLeftTex) {
      const leftCanvas = generateProceduralTexture('head_side', {
        skinColor,
        hairColor,
        hairStyle,
        side: 'left'
      });
      const ctx = this.headSideLeftCanvas.getContext('2d');
      ctx.clearRect(0, 0, this.headSideLeftCanvas.width, this.headSideLeftCanvas.height);
      ctx.drawImage(leftCanvas, 0, 0);
      this.headSideLeftTex.needsUpdate = true;
    }

    // 4. Cập nhật mặt sau gáy (-Z)
    if (this.headBackCanvas && this.headBackTex) {
      const backCanvas = generateProceduralTexture('head_back', {
        skinColor,
        hairColor,
        hairStyle
      });
      const ctx = this.headBackCanvas.getContext('2d');
      ctx.clearRect(0, 0, this.headBackCanvas.width, this.headBackCanvas.height);
      ctx.drawImage(backCanvas, 0, 0);
      this.headBackTex.needsUpdate = true;
    }

    // 5. Cập nhật đỉnh đầu (+Y)
    if (this.headTopMat) {
      this.headTopMat.color.set(hairStyle === 'bald' ? skinColor : hairColor);
    }
  }

  /**
   * Tạo bóng thoại (Speech Bubble Sprite) lơ lửng bám đầu nhân vật
   */
  createSpeechBubble() {
    createSpeechBubble(this);
  }

  speak(text, durationMs = 5000) {
    speak(this, text, durationMs);
  }

  hideSpeech() {
    hideSpeech(this);
  }

  /**
   * Giới tính & độ tuổi dùng để vẽ khuôn mặt. Ưu tiên khai báo trong preset (gender / age),
   * nếu thiếu thì suy ra từ dáng người, trang phục và kiểu tóc.
   */
  getFaceProfile() {
    const c = this.config;
    const bodyType = c.bodyType || 'normal';
    const age = c.age || (bodyType === 'child' ? 'child' : bodyType === 'elder' ? 'elder' : 'adult');
    const outfit = c.features?.outfit || '';
    const looksFemale = /_female$/.test(outfit) || outfit === 'ao_dai' || c.features?.hairStyle === 'long_hair';
    const gender = c.gender || (looksFemale ? 'female' : 'male');
    return { gender, age };
  }

  setExpression(expression) {
    if (!this.faceCanvas || !this.faceTex) return;
    if (this.currentExpression === expression) return;
    this.currentExpression = expression;
    const speciesIdForFace = this.config.speciesId || this.config.species || this.config.features?.speciesId || '';
    const canvas = generateProceduralTexture('face', {
      skinColor: this.config.colors.skin,
      eyeColor: this.config.colors.eyes,
      hairColor: this.config.colors.hair,
      hairStyle: this.config.features.hairStyle,
      hasBlush: this.config.features.hasBlush,
      eyeType: this.config.features.eyeType,
      speciesId: speciesIdForFace,
      hasGlasses: false,
      expression,
      ...this.getFaceProfile()
    });
    const ctx = this.faceCanvas.getContext('2d');
    ctx.clearRect(0, 0, this.faceCanvas.width, this.faceCanvas.height);
    ctx.drawImage(canvas, 0, 0);
    this.faceTex.needsUpdate = true;
  }

  playAction(actionName) {
    if (this.currentActionName === actionName) return;
    this.currentActionName = actionName;

    // Tự động gán biểu cảm gương mặt tương thích với từng cử động
    if (actionName === 'explain' || actionName === 'present') {
      this.setExpression('talking');
    } else if (actionName === 'think') {
      this.setExpression('think');
    } else if (actionName === 'cheer') {
      this.setExpression('laugh');
    } else if (actionName === 'jump') {
      this.setExpression('laugh'); // Bật nhảy - Cười lớn
    } else if (actionName === 'run') {
      this.setExpression('happy'); // Chạy nhanh - Vui tươi
    } else if (actionName === 'stumble') {
      this.setExpression('dizzy'); // Loạng choạng - Choáng váng
    } else if (actionName === 'roll_ground') {
      this.setExpression('neutral'); // Lăn tròn đất - Neutral
    } else if (actionName === 'apologize') {
      this.setExpression('embarrassed');
    } else if (actionName === 'crawl' || actionName === 'crouch_low') {
      this.setExpression('neutral');
    } else if (actionName === 'fall_backward' || actionName === 'fall_forward') {
      this.setExpression('dizzy');
    } else if (actionName === 'sit_ground' || actionName === 'stand_up') {
      this.setExpression('happy');
    } else if (actionName === 'thank_you' || actionName === 'bow' || actionName === 'greet' || actionName === 'wave') {
      this.setExpression('happy');
    } else if (actionName === 'check_phone') {
      this.setExpression('think');
    } else if (actionName === 'traffic_direct' || actionName === 'hands_behind_back' || actionName === 'carry_box') {
      this.setExpression('neutral');
    } else if (actionName === 'sip_drink') {
      this.setExpression('happy');
    } else {
      this.setExpression('happy');
    }
    this.animator.play(actionName);
  }

  /**
   * Tạo Fake Blob Shadow mờ mềm mại dưới chân nhân vật
   */
  createBlobShadow() {
    const { torsoW, torsoD } = this.dimensions;
    const shadowW = torsoW * 2.0;
    const shadowD = torsoD * 2.6;

    const shadowGeo = new THREE.PlaneGeometry(shadowW, shadowD);
    shadowGeo.rotateX(-Math.PI / 2);

    const shadowCanvas = generateProceduralTexture('blob_shadow', { size: 256 });
    const shadowTex = new THREE.CanvasTexture(shadowCanvas);
    shadowTex.colorSpace = THREE.SRGBColorSpace;

    const shadowMat = new THREE.MeshBasicMaterial({
      map: shadowTex,
      transparent: true,
      opacity: 0.65,
      depthWrite: false
    });

    const blobShadow = new THREE.Mesh(shadowGeo, shadowMat);
    blobShadow.name = 'BlobShadow';
    blobShadow.position.set(0, 0.003, 0);
    blobShadow.castShadow = false;
    blobShadow.receiveShadow = false;

    this.root.add(blobShadow);
    this.parts.blobShadow = blobShadow;
    this.disposables.push(shadowGeo, shadowTex, shadowMat);
  }

  /**
   * Thực thi chính sách Shadow:
   * - 100% Mesh cơ thể và phụ kiện: castShadow = false (không tự đổ bóng lên cơ thể), receiveShadow = true (nhận bóng râm môi trường)
   * - Blob Shadow dưới chân: castShadow = false, receiveShadow = false
   */
  enforceShadowPolicy() {
    enforceShadowPolicy(this.root, {
      blobShadow: this.parts && this.parts.blobShadow,
      capMetalness: true
    });
  }

  /**
   * Áp dụng cấu hình diện mạo mới động cho nhân vật
   * @param {object} newConfig - Cấu hình diện mạo mới
   */
  applyConfig(newConfig = {}) {
    if (!newConfig) return;
    if (newConfig.colors) {
      this.config.colors = { ...this.config.colors, ...newConfig.colors };
    }
    if (newConfig.features) {
      this.config.features = { ...this.config.features, ...newConfig.features };
    }
    if (newConfig.bodyType) this.config.bodyType = newConfig.bodyType;
    if (newConfig.scale) this.config.scale = newConfig.scale;

    this.applyInitialAttachments();
    this.updateHeadTextures();
    if (this.config.features && this.config.features.outfit) {
      this.setOutfit(this.config.features.outfit, this.config.colors.shirt);
    }
    if (this.config.colors && this.config.colors.pants) {
      this.setPants('long', this.config.colors.pants);
    }
    this.enforceShadowPolicy();
  }

  update(delta) {
    this.animator.update(delta);
    this.groundFeet();
    this.updateAoDaiFlaps();
    if (this.speechSprite && this.parts.pelvis) {
      this.speechSprite.position.y = this.parts.pelvis.position.y + 1.4;
    }
  }

  /**
   * Bám đất: nhiều tư thế (thở, vẫy tay, cúi chào, thuyết trình...) hạ hông trong khi chân thẳng,
   * làm bàn chân lún xuống sàn. Đo điểm thấp nhất của 2 đế giày và nâng hông lên đúng phần lún.
   */
  groundFeet() {
    if (!this.soleMeshes || !this.soleMeshes.length || !this.parts.pelvis) return;
    this.root.updateMatrixWorld(true);
    if (!this._rootInv) {
      this._rootInv = new THREE.Matrix4();
      this._tmpV = new THREE.Vector3();
    }
    this._rootInv.copy(this.root.matrixWorld).invert();
    const v = this._tmpV;
    let minY = Infinity;
    this.soleMeshes.forEach((mesh) => {
      const pos = mesh.geometry.attributes.position;
      for (let i = 0; i < pos.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld).applyMatrix4(this._rootInv);
        if (v.y < minY) minY = v.y;
      }
    });
    if (minY < 0) {
      this.parts.pelvis.position.y -= minY;
    }
  }

  dispose() {
    this.hideSpeech();
    this.itemsLib.dispose();
    disposeObject3D(this.root, { disposeTextures: true });
    this.disposables.forEach((item) => {
      if (item && typeof item.dispose === 'function') {
        item.dispose();
      }
    });
    this.disposables = [];
  }
}

registerAvatarAccessoriesMethods(BlockyAvatar.prototype);
registerAvatarOutfitMethods(BlockyAvatar.prototype);
registerAvatarHeldItemMethods(BlockyAvatar.prototype);
